/**
 * Integrations Page.
 *
 * Manage e-commerce platform connections (Shopify, eBay).
 */

import { useState } from "react";
import { Globe, Plus, Trash2 } from "lucide-react";
import {
  useIntegrations,
  useCreateIntegration,
  useDeleteIntegration,
} from "@/features/integrations/api";
import { toast } from "@/stores/toastStore";
import { PLATFORMS } from "@/types/integration";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

function IntegrationsPage() {
  const { data: integrations, isLoading } = useIntegrations();
  const createMutation = useCreateIntegration();
  const deleteMutation = useDeleteIntegration();

  const [showCreate, setShowCreate] = useState(false);
  const [platform, setPlatform] = useState("shopify");
  const [name, setName] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [accessToken, setAccessToken] = useState("");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const credentials: Record<string, string> = {};
    if (platform === "shopify") {
      credentials.store_url = storeUrl;
      credentials.access_token = accessToken;
    } else if (platform === "ebay") {
      credentials.app_id = accessToken;
    }

    try {
      await createMutation.mutateAsync({ platform, name, credentials });
      setShowCreate(false);
      setName("");
      setStoreUrl("");
      setAccessToken("");
      toast.success("Integration connected", `${name} has been connected.`);
    } catch {
      toast.error("Failed to connect integration");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Integrations
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Connect e-commerce platforms for multi-channel listing.
          </p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> Connect Platform
            </>
          )}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)]">
              Connect Platform
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Platform
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-sm"
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Display Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder={`My ${platform === "shopify" ? "Shopify" : "eBay"} Store`}
              />
              {platform === "shopify" && (
                <Input
                  label="Store URL"
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value)}
                  placeholder="mystore.myshopify.com"
                />
              )}
              <Input
                label={platform === "shopify" ? "Access Token" : "App ID"}
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Enter API credential"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" isLoading={createMutation.isPending}>
                Connect
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : integrations && integrations.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.map((integ) => {
            const platformInfo = PLATFORMS.find(
              (p) => p.value === integ.platform,
            );
            return (
              <Card key={integ.id}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] text-white text-xs font-bold"
                      style={{
                        backgroundColor: platformInfo?.color || "#6B7280",
                      }}
                    >
                      {integ.platform.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-medium text-[var(--text-primary)]">
                        {integ.name}
                      </h3>
                      <p className="text-xs text-[var(--text-secondary)] capitalize">
                        {integ.platform}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={integ.is_active ? "success" : "default"}>
                      {integ.is_active ? "Connected" : "Inactive"}
                    </Badge>
                    <button
                      onClick={() =>
                        deleteMutation.mutate(integ.id, {
                          onSuccess: () => toast.success("Integration removed"),
                          onError: () =>
                            toast.error("Failed to remove integration"),
                        })
                      }
                      className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 text-xs text-[var(--text-tertiary)]">
                  {integ.has_credentials
                    ? "Credentials configured"
                    : "No credentials"}
                  {integ.last_sync_at && (
                    <>
                      {" "}
                      &middot; Last sync:{" "}
                      {new Date(integ.last_sync_at).toLocaleDateString()}
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Globe />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No integrations
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Connect Shopify or eBay to start listing inventory.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default IntegrationsPage;
